import { useEffect, useRef } from "react";
import { useModal } from "../../hooks";
import { useRecoilValue } from "recoil";
import { useLogin } from "../authentication/useLogin";
import { authState } from "../recoil";

export function TimeoutConfig() {
    const modal = useModal();
    const eventListenersRef = useRef<{ event: string; handler: () => void }[]>([]);
    const isModalOpenRef = useRef(false);
    const auth = useRecoilValue(authState);
    const { logout } = useLogin();
    const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    const WARNING_TIMEOUT = 5 * 60 * 1000; // 5 minutes

    // 1. Function to check for inactivity and logout
    const checkForInactivity = () => {
        const currentExpireTime = localStorage.getItem("expireTime");
        if (currentExpireTime) {
            const expireTimeNum = Number(currentExpireTime);
            const timeLeft = expireTimeNum - Date.now();

            if (timeLeft <= WARNING_TIMEOUT && timeLeft > 0 && !isModalOpenRef.current) {
                showWarningModal();
            } else if (expireTimeNum < Date.now()) {
                console.log("logged out");
                localStorage.removeItem("expireTime");
                modal.closeModal();
                logout();
            }
        }
    };

    // 2. Show warning modal 5 seconds before logout
    const showWarningModal = () => {
        isModalOpenRef.current = true;

        // Remove all event listeners
        eventListenersRef.current.forEach(({ event, handler }) => {
            window.removeEventListener(event, handler);
        });

        modal.openModal({
            title: "Session About to Expire",
            content: "Your session will end in 30 seconds. Extend your session?",
            confirmText: "Extend Session",
            cancelText: "Logout Now",
            onConfirm: () => {
                updateExpiredTime();
                console.log("Session extended by user");
                restoreEventListeners();
            },
            onCancel: () => {
                forceLogout();
            },
            // onClose: () => {
            //     forceLogout();
            // }
        });
    };

    const forceLogout = () => {
        localStorage.removeItem("expireTime");
        logout();
    };

    // 3. Restore event listeners after modal closes
    const restoreEventListeners = () => {
        if (isModalOpenRef.current) {
            isModalOpenRef.current = false;
            eventListenersRef.current.forEach(({ event, handler }) => {
                window.addEventListener(event, handler);
            });
        }
    };

    // 4. Function to update expire time
    const updateExpiredTime = () => {
        const newExpireTime = Date.now() + SESSION_TIMEOUT;
        localStorage.setItem("expireTime", newExpireTime.toString());
    };

    // 5. Effect to set interval to check for inactivity
    useEffect(() => {
        if (!auth.isSignedIn) return;

        const interval = setInterval(() => {
            checkForInactivity();
        }, 1000);

        return () => clearInterval(interval);
    }, [auth.isSignedIn]);

    // 6. Update expire time on any user activity
    useEffect(() => {
        if (!auth.isSignedIn) return;

        // Set initial update time if none exists
        if (!localStorage.getItem("expireTime")) {
            updateExpiredTime();
        }

        // Create handlers
        const events = ["click", "keypress", "scroll", "mousemove"];
        const handlers = events.map((event) => ({
            event,
            handler: () => {
                if (!isModalOpenRef.current) {
                    updateExpiredTime();
                }
            },
        }));

        // Store for later removal
        eventListenersRef.current = handlers;

        handlers.forEach(({ event, handler }) => {
            window.addEventListener(event, handler);
        });

        return () => {
            handlers.forEach(({ event, handler }) => {
                window.removeEventListener(event, handler);
            });
        };
    }, [auth.isSignedIn]);

    return null;
}
